import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { Product, Category } from '../types';
import { apiService } from '../services/api';
import ProductForm from './ProductForm';
import { useToast } from '../context/ToastContext';

interface ProductListProps {
  onNavigateToCategories?: () => void;
}

const PRODUCT_LISTING_URL = import.meta.env.VITE_PRODUCT_LISTING_URL;

const ProductList: React.FC<ProductListProps> = ({ onNavigateToCategories }) => {
  const PRODUCT_HELP_BANNER_DISMISSED_KEY = 'product_help_banner_dismissed';
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | undefined>();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [activeSearchText, setActiveSearchText] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [editingSortOrder, setEditingSortOrder] = useState<Record<string, string>>({});
  const [showProductHelpBanner, setShowProductHelpBanner] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return localStorage.getItem(PRODUCT_HELP_BANNER_DISMISSED_KEY) !== 'true';
  });
  /** Increment to refetch the listing after create/update/delete (also resets to page 1). */
  const [refreshListTrigger, setRefreshListTrigger] = useState(0);
  const [autoImageLoadingKey, setAutoImageLoadingKey] = useState<string | null>(null);
  const { showError, showSuccess } = useToast();

  const productHasImage = (product: Product) =>
    Array.isArray(product.images) && product.images.some((img) => typeof img === 'string' && img.trim() !== '');

  const getProductActionKey = (product: Product) =>
    product._id || `${product.name}::${JSON.stringify(product.images ?? [])}`;

  const refreshProductList = () => {
    setPage(1);
    setRefreshListTrigger((t) => t + 1);
  };

  const handleDismissProductHelpBanner = () => {
    setShowProductHelpBanner(false);
    localStorage.setItem(PRODUCT_HELP_BANNER_DISMISSED_KEY, 'true');
  };

  const normalizeProduct = (raw: Product): Product => {
    const rawCategory = (raw as Product & { category?: string | string[] }).category;
    const normalizedCategory = Array.isArray(rawCategory)
      ? rawCategory
      : typeof rawCategory === 'string' && rawCategory
        ? [rawCategory]
        : [];

    return {
      ...raw,
      category: normalizedCategory as unknown as Product['category'],
      images: Array.isArray(raw.images) ? raw.images : [],
      quantity: typeof raw.quantity === 'number' ? raw.quantity : 0,
    };
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiService.getCategories();
        if (res.status) {
          const raw = Array.isArray(res.data) ? res.data : Array.isArray(res.doc) ? res.doc : [];
          const active = raw.filter((c: Category) => {
            const s = (c as Category).status;
            if (s === undefined || s === null) return true;
            if (typeof s === 'number') return s === 1;
            if (typeof s === 'string') return s === '1';
            return Boolean(s);
          });
          setCategories(active);
        }
      } catch {
        // ignore
      }
    };
    load();
  }, []);

  const handleSortOrderChange = async (product: Product, newValue: string) => {
    if (!product._id) return;
    const trimmed = newValue.trim();
    const parsed = trimmed === '' ? 0 : parseInt(trimmed, 10);
    if (isNaN(parsed)) {
      showError('Product', 'Invalid sort order');
      return;
    }
    const prev = product.sortOrder ?? 0;
    if (parsed === prev) return;
    setProducts((p) => p.map((x) => (x._id === product._id ? { ...x, sortOrder: parsed } : x)));
    try {
      const res = await apiService.updateProduct(product._id, { _id: product._id, sortOrder: parsed });
      if (res.status) {
        showSuccess('Product', 'Sort order updated');
      } else {
        setProducts((p) => p.map((x) => (x._id === product._id ? { ...x, sortOrder: prev } : x)));
        showError('Product', res.message || 'Failed to update sort order');
      }
    } catch {
      setProducts((p) => p.map((x) => (x._id === product._id ? { ...x, sortOrder: prev } : x)));
      showError('Product', 'Failed to update sort order');
    }
  };

  // Ensure page starts at top when visiting this screen
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    const isInfiniteLoad = page > 1 && window.innerWidth < 768;
    fetchProducts(isInfiniteLoad);
  }, [page, limit, activeSearchText, activeCategory, refreshListTrigger]);

  const fetchProducts = async (isLoadMore: boolean = false) => {
    if (isLoadMore) {
      setIsLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const authToken = localStorage.getItem('auth_token') || '';
      let shopId = '';
      if (authToken) {
        try {
          const payload = authToken.split('.')[1] || '';
          const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
          const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
          const parsed = JSON.parse(atob(padded));
          shopId = parsed?.data?.shopId || '';
        } catch {
          shopId = '';
        }
      }
      const listingUrl = shopId
        ? `${PRODUCT_LISTING_URL}?${new URLSearchParams({ shopId: String(shopId) }).toString()}`
        : PRODUCT_LISTING_URL;

      const listingResponse = await fetch(listingUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });
      const response: any = await listingResponse.json();

      if (response?.status) {
        const rawProducts = Array.isArray(response.data.products) ? response.data.products : [];
        const normalizedProducts = rawProducts.map((product: Product) => normalizeProduct(product));

        const trimmedSearch = activeSearchText.trim().toLowerCase();
        const trimmedCategory = activeCategory.trim().toLowerCase();
        const filteredProducts = normalizedProducts.filter((product: Product) => {
          const matchesSearch = !trimmedSearch
            || product.name?.toLowerCase().includes(trimmedSearch)
            || product.longDescription?.toLowerCase().includes(trimmedSearch);

          const productCategories = Array.isArray(product.category)
            ? product.category
            : typeof product.category === 'string'
              ? [product.category]
              : [];
          const matchesCategory = !trimmedCategory
            || productCategories.some((c) => String(c).toLowerCase() === trimmedCategory);

          return matchesSearch && matchesCategory;
        });

        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const incoming = filteredProducts.slice(startIndex, endIndex);

        if (isLoadMore) {
          setProducts(prev => [...prev, ...incoming]);
        } else {
          setProducts(incoming);
        }
        const totalCount = filteredProducts.length;
        setTotalProducts(totalCount);
        const computedTotalPages = Math.max(1, Math.ceil(totalCount / limit));
        setTotalPages(computedTotalPages);
      } else {
        showError('Products', response?.error || response?.message || 'Failed to fetch products');
      }
    } catch (err) {
      showError('Products', 'An error occurred while fetching products');
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Infinite scroll (mobile only)
  const scrollRef = React.useRef({
    isLoadingMore,
    page,
    totalPages,
    productsLength: products.length,
  });

  React.useEffect(() => {
    scrollRef.current = {
      isLoadingMore,
      page,
      totalPages,
      productsLength: products.length,
    };
  }, [isLoadingMore, page, totalPages, products.length]);

  const hasScrolledRef = React.useRef(false);
  const isPageIncrementingRef = React.useRef(false);

  // Reset the increment guard when loading completes
  useEffect(() => {
    if (!isLoadingMore) {
      isPageIncrementingRef.current = false;
    }
  }, [isLoadingMore]);

  useEffect(() => {
    const handleScroll = () => {
      hasScrolledRef.current = true;
      if (window.innerWidth >= 768) return; // only mobile
      if (isPageIncrementingRef.current) return; // prevent multiple triggers
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const distanceFromBottom = documentHeight - (scrollTop + windowHeight);
      const { isLoadingMore, page, totalPages, productsLength } = scrollRef.current;
      if (distanceFromBottom <= 200 && !isLoadingMore && page < totalPages && productsLength > 0) {
        isPageIncrementingRef.current = true;
        setPage(prev => prev + 1);
      }
    };

    const poll = () => {
      if (window.innerWidth >= 768) return;
      if (!hasScrolledRef.current) return;
      if (isPageIncrementingRef.current) return; // prevent multiple triggers
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const distanceFromBottom = documentHeight - (scrollTop + windowHeight);
      const { isLoadingMore, page, totalPages, productsLength } = scrollRef.current;
      if (distanceFromBottom <= 200 && !isLoadingMore && page < totalPages && productsLength > 0) {
        isPageIncrementingRef.current = true;
        setPage(prev => prev + 1);
      }
    };

    const pollInterval = setInterval(poll, 500);
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll);
      clearInterval(pollInterval);
    };
  }, []);

  const handleDelete = async (product: Product) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    // If product doesn't have _id, reload from API first
    if (!product._id) {
      try {
        const reloadedProducts = await apiService.getProducts();
        if (reloadedProducts.status && reloadedProducts.data) {
          setProducts(reloadedProducts.data);
          // Try to find the product by name and images
          const productWithId = reloadedProducts.data.find(
            p => p.name === product.name && 
                 JSON.stringify(p.images) === JSON.stringify(product.images)
          );
          if (productWithId && productWithId._id) {
            // Now delete with the correct _id
            try {
              const response = await apiService.deleteProduct(productWithId._id);
              if (response.status) {
                refreshProductList();
                showSuccess('Product', 'Product deleted successfully');
              } else {
                showError('Product', response.message || 'Failed to delete product');
              }
            } catch (err) {
              showError('Product', 'An error occurred while deleting the product');
            }
          } else {
            showError('Product', 'Product not found. It may have already been deleted.');
          }
        } else {
          showError('Products', 'Failed to reload products. Please try again.');
        }
      } catch (err) {
        showError('Products', 'An error occurred while reloading products.');
      }
      return;
    }

    try {
      const response = await apiService.deleteProduct(product._id);
      if (response.status) {
        refreshProductList();
        showSuccess('Product', 'Product deleted successfully');
      } else {
        showError('Product', response.message || 'Failed to delete product');
      }
    } catch (err) {
      showError('Product', 'An error occurred while deleting the product');
    }
  };

  const handleEdit = async (product: Product) => {
    // If product doesn't have _id, reload from API first
    if (!product._id) {
      try {
        const reloadedProducts = await apiService.getProducts();
        if (reloadedProducts.status && reloadedProducts.data) {
          setProducts(reloadedProducts.data);
          // Try to find the product by name (or other unique identifier)
          const productWithId = reloadedProducts.data.find(
            p => p.name === product.name && 
                 JSON.stringify(p.images) === JSON.stringify(product.images)
          );
          if (productWithId) {
            setEditingProduct(productWithId);
            setIsFormOpen(true);
            return;
          }
        }
        showError('Product', 'Product not found. Please try again.');
      } catch (err) {
        showError('Products', 'An error occurred while reloading products.');
      }
      return;
    }

    setEditingProduct(product);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingProduct(undefined);
  };

  const handleFormSuccess = (_product: Product, _mode: 'create' | 'update') => {
    refreshProductList();
  };

  const resolveProductWithId = async (product: Product): Promise<Product | null> => {
    if (product._id) return product;
    try {
      const reloadedProducts = await apiService.getProducts();
      if (reloadedProducts.status && reloadedProducts.data) {
        const productWithId = reloadedProducts.data.find(
          (p) =>
            p.name === product.name &&
            JSON.stringify(p.images) === JSON.stringify(product.images)
        );
        return productWithId ?? null;
      }
    } catch {
      /* handled by caller */
    }
    return null;
  };

  const handleAutoAssignImage = async (product: Product) => {
    if (productHasImage(product)) return;

    const searchName = product.name?.trim();
    if (!searchName) {
      showError('Product', 'Product name is required to search for an image');
      return;
    }

    let target = product;
    if (!target._id) {
      const resolved = await resolveProductWithId(product);
      if (!resolved?._id) {
        showError('Product', 'Product not found. Please try again.');
        return;
      }
      target = resolved;
    }

    const loadingKey = getProductActionKey(product);
    setAutoImageLoadingKey(loadingKey);
    try {
      const searchResult = await apiService.searchFoodImages(searchName, { orientation: 'landscape' });
      if (!searchResult.status || !Array.isArray(searchResult.data) || searchResult.data.length === 0) {
        showError('Image', searchResult.message || 'No images found for this product');
        return;
      }

      const firstImage = searchResult.data[0];
      const downloadResponse = await fetch(firstImage.url);
      if (!downloadResponse.ok) {
        throw new Error('Failed to download image');
      }
      const blob = await downloadResponse.blob();
      const mimeType = blob.type && blob.type.startsWith('image/') ? blob.type : 'image/jpeg';
      const extension = mimeType === 'image/png' ? 'png' : 'jpg';
      const file = new File([blob], `food-${firstImage.id}.${extension}`, { type: mimeType });

      const uploadResult = await apiService.uploadImage(file, 'productImage');
      const imagePath =
        uploadResult.status === true &&
        uploadResult.data &&
        typeof (uploadResult.data as { path?: string }).path === 'string'
          ? (uploadResult.data as { path: string }).path
          : null;

      if (!imagePath) {
        showError('Image', uploadResult.message || 'Failed to upload image');
        return;
      }

      const updateResult = await apiService.updateProduct(target._id!, {
        _id: target._id,
        images: [imagePath],
      });

      if (updateResult.status) {
        setProducts((prev) =>
          prev.map((p) => (p._id === target._id ? { ...p, images: [imagePath] } : p))
        );
        showSuccess('Product', 'Product image added successfully');
      } else {
        showError('Product', updateResult.message || 'Failed to update product');
      }
    } catch {
      showError('Image', 'Failed to assign image. Please try again.');
    } finally {
      setAutoImageLoadingKey(null);
    }
  };

  const formatCategory = (category?: string) => {
    if (typeof category !== 'string' || !category) return 'Uncategorized';
    return category
      .split('/')
      .filter(Boolean)
      .map(s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
      .join(' > ');
  };

  const handleSearch = () => {
    setActiveSearchText(searchText);
    setPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchText('');
    setActiveSearchText('');
    setPage(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 overflow-x-hidden">
      {/* Mobile-friendly header */}
      <div className="flex flex-col space-y-3">
        {/* Search Bar and Category Filter */}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="flex-1 flex flex-wrap gap-2 max-w-full lg:max-w-2xl items-center">
            <select
              value={activeCategory}
              onChange={(e) => {
                setActiveCategory(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-orange-600 focus:border-orange-600 bg-white min-w-[140px] max-w-[39vw] md:max-w-none"
            >
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c._id} value={c.name || ''}>{c.name}</option>
              ))}
            </select>
            <div className="relative flex-1 min-w-0">
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Search products..."
                className="w-full px-4 py-2 pl-10 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-orange-600 focus:border-orange-600 shadow-sm"
              />
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors inline-flex items-center justify-center flex-shrink-0"
            >
              <MagnifyingGlassIcon className="h-5 w-5" />
            </button>
            {searchText && (
              <button
                onClick={handleClearSearch}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors flex-shrink-0"
              >
                Clear
              </button>
            )}
          </div>
          <button
            onClick={() => setIsFormOpen(true)}
            className="w-full sm:w-auto sm:ml-auto inline-flex items-center justify-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Product
          </button>
        </div>
      </div>

      {/* WhatsApp Help Banner */}
      {showProductHelpBanner && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 relative">
          <button
            type="button"
            aria-label="Dismiss help banner"
            onClick={handleDismissProductHelpBanner}
            className="absolute top-2 right-2 w-7 h-7 rounded-full text-green-800 hover:bg-green-100 inline-flex items-center justify-center"
          >
            ×
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pr-8">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-green-900">Need help creating your menu?</h4>
                <p className="text-sm text-green-700 mt-1">
                  Share your menu image (photo/PDF) on WhatsApp and we'll help you set it up!
                </p>
              </div>
            </div>
            <a
              href="https://wa.me/917907147438?text=Hi%2C%20I%20need%20help%20creating%20my%20menu.%20Here%20is%20my%20menu%20image."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors flex-shrink-0"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Chat on WhatsApp
            </a>
          </div>
        </div>
      )}

      {/* Mobile Card Layout */}
      <div className="block md:hidden space-y-4">
        {products.map((product) => (
          <div key={product._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-start space-x-3 mb-3">
              {product.images && product.images.length > 0 ? (
                <div className="w-16 h-16 rounded-lg flex-shrink-0 border border-gray-300 bg-gray-100 flex items-center justify-center overflow-hidden">
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    onError={e => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent && !parent.querySelector('.error-text')) {
                        const errorText = document.createElement('span');
                        errorText.className = 'error-text text-xs text-gray-500 text-center px-1';
                        errorText.textContent = 'Image not found';
                        parent.appendChild(errorText);
                      }
                    }}
                  />
                  <span className="error-text text-xs text-gray-500 text-center px-1 hidden">Image not found</span>
                </div>
              ) : null}
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 whitespace-normal break-words">{product.name}</h3>
                {/* <p className="text-sm text-gray-600">{product.condition}</p> */}
                {product.images && product.images.length > 1 && (
                  <p className="text-xs text-gray-500">+{product.images.length - 1} more images</p>
                )}
              </div>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                 product.outofstock == true
                  ? 'bg-red-100 text-red-800':'bg-green-100 text-green-800' 
              }`}>
                {product.outofstock == true ? 'Out of Stock':'In Stock'}
              </span>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex justify-between items-center gap-2 min-w-0">
                <span className="text-sm font-medium text-gray-700">Sort:</span>
                {product._id ? (
                  <input
                    type="number"
                    value={editingSortOrder[product._id!] ?? (product.sortOrder != null ? String(product.sortOrder) : '')}
                    onChange={(e) => setEditingSortOrder((p) => ({ ...p, [product._id!]: e.target.value }))}
                    onBlur={() => {
                      const raw = editingSortOrder[product._id!] ?? (product.sortOrder != null ? String(product.sortOrder) : '');
                      setEditingSortOrder((p) => {
                        const n = { ...p };
                        delete n[product._id!];
                        return n;
                      });
                      handleSortOrderChange(product, raw);
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                    className="w-16 px-2 py-1 text-sm text-right border border-gray-300 rounded focus:outline-none focus:ring-orange-600 focus:border-orange-600"
                  />
                ) : (
                  <span className="text-sm text-gray-900">{product.sortOrder ?? '—'}</span>
                )}
              </div>
              <div className="flex justify-between items-center gap-2 min-w-0">
                <span className="text-sm font-medium text-gray-700">Category:</span>
                <span className="text-sm text-gray-900 min-w-0 max-w-[65%] truncate text-right">
                  {formatCategory(product.category[0])}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Price:</span>
                <div className="text-right">
                  {product.portions && product.portions.length > 0 ? (
                    <div className="text-sm font-semibold text-gray-900">Variant Pricing</div>
                  ) : (
                    <>
                      <div className="text-sm font-semibold text-gray-900">₹{product.price}</div>
                      <div className="text-xs text-gray-500">Offer: ₹{product.offerPrice}</div>
                    </>
                  )}
                </div>
              </div>
              {/* <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Stock:</span>
                <span className="text-sm text-gray-900">{product.quantity}</span>
              </div> */}
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => handleEdit(product)}
                className="flex-1 inline-flex items-center justify-center p-2 border border-transparent rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
              >
                <PencilIcon className="h-4 w-4 mr-1" />
                Edit
              </button>
              <button
                type="button"
                onClick={() => handleAutoAssignImage(product)}
                disabled={productHasImage(product) || autoImageLoadingKey === getProductActionKey(product)}
                title={
                  productHasImage(product)
                    ? 'Product already has an image'
                    : 'Search and add the first matching food image'
                }
                className="inline-flex items-center justify-center p-2 border border-transparent rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-emerald-600"
              >
                {autoImageLoadingKey === getProductActionKey(product) ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <PhotoIcon className="h-4 w-4" />
                )}
              </button>
              <button
                onClick={() => handleDelete(product)}
                className="flex-1 inline-flex items-center justify-center p-2 border border-transparent rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
              >
                <TrashIcon className="h-4 w-4 mr-1" />
                Delete
              </button>
            </div>
          </div>
        ))}
        {/* Infinite scroll sentinel - only on mobile when there are more pages */}
        {page < totalPages && (
          <div
            id="product-infinite-scroll-sentinel"
            className="h-1 w-full"
            ref={(el) => {
              if (el && window.innerWidth < 768) {
                const observer = new IntersectionObserver(
                  (entries) => {
                    entries.forEach((entry) => {
                      if (entry.isIntersecting && !isPageIncrementingRef.current) {
                        const { isLoadingMore, page, totalPages, productsLength } = scrollRef.current;
                        if (!isLoadingMore && page < totalPages && productsLength > 0) {
                          isPageIncrementingRef.current = true;
                          setPage(prev => prev + 1);
                        }
                      }
                    });
                  },
                  {
                    root: null,
                    rootMargin: '100px',
                    threshold: 0.1,
                  }
                );
                observer.observe(el);
              }
            }}
          />
        )}
        {isLoadingMore && (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
          </div>
        )}
      </div>

      {/* Desktop Table Layout */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sort
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stock
                </th> */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {products.map((product) => (
                <tr key={product._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {product._id ? (
                      <input
                        type="number"
                        value={editingSortOrder[product._id!] ?? (product.sortOrder != null ? String(product.sortOrder) : '')}
                        onChange={(e) => setEditingSortOrder((p) => ({ ...p, [product._id!]: e.target.value }))}
                        onBlur={() => {
                          const raw = editingSortOrder[product._id!] ?? (product.sortOrder != null ? String(product.sortOrder) : '');
                          setEditingSortOrder((p) => {
                            const n = { ...p };
                            delete n[product._id!];
                            return n;
                          });
                          handleSortOrderChange(product, raw);
                        }}
                        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                        className="w-14 px-2 py-1 text-sm text-right border border-gray-300 rounded focus:outline-none focus:ring-orange-600 focus:border-orange-600"
                      />
                    ) : (
                      <span className="text-sm text-gray-500">{product.sortOrder ?? '—'}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="flex items-center">
                        {product.images && product.images.length > 0 ? (
                          <div className="w-10 h-10 rounded-lg mr-3 border border-gray-300 bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                            <img
                              src={product.images[0]}
                              alt={product.name}
                              className="w-full h-full object-cover"
                              onError={e => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent && !parent.querySelector('.error-text')) {
                                  const errorText = document.createElement('span');
                                  errorText.className = 'error-text text-xs text-gray-500 text-center px-1';
                                  errorText.textContent = 'Image not found';
                                  parent.appendChild(errorText);
                                }
                              }}
                            />
                            <span className="error-text text-xs text-gray-500 text-center px-1 hidden">Image not found</span>
                          </div>
                        ) : null}
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 max-w-[240px] truncate">{product.name}</div>
                          {product.images && product.images.length > 1 && (
                            <div className="text-xs text-gray-500">+{product.images.length - 1} more</div>
                          )}
                        </div>
                      </div>
                      {/* <div className="text-sm text-gray-500">{product.condition}</div> */}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatCategory(product.category[0])}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {product.portions && product.portions.length > 0 ? (
                      <div className="text-sm font-semibold text-gray-900">Variant Pricing</div>
                    ) : (
                      <>
                        <div className="text-sm text-gray-900">₹{product.price}</div>
                        <div className="text-sm text-gray-500">Offer: ₹{product.offerPrice}</div>
                      </>
                    )}
                  </td>
                  {/* <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{product.quantity}</div>
                  </td> */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                       product.outofstock == true
                        ? 'bg-red-100 text-red-800':'bg-green-100 text-green-800' 
                    }`}>
                      {product.outofstock == true ? 'Out of Stock':'In Stock'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(product)}
                        className="text-orange-600 hover:text-orange-900"
                        title="Edit product"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAutoAssignImage(product)}
                        disabled={productHasImage(product) || autoImageLoadingKey === getProductActionKey(product)}
                        title={
                          productHasImage(product)
                            ? 'Product already has an image'
                            : 'Search and add the first matching food image'
                        }
                        className="text-emerald-600 hover:text-emerald-900 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-emerald-600"
                      >
                        {autoImageLoadingKey === getProductActionKey(product) ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600" />
                        ) : (
                          <PhotoIcon className="h-5 w-5" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(product)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete product"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {products.length === 0 && !loading && (
        <div className="text-center py-12">
          {activeSearchText ? (
            <div>
              <p className="text-gray-500">No products found matching "{activeSearchText}".</p>
              <button
                onClick={handleClearSearch}
                className="mt-2 text-orange-600 hover:text-orange-700 underline"
              >
                Clear search
              </button>
            </div>
          ) : (
            <p className="text-gray-500">No products found. Add your first product to get started.</p>
          )}
        </div>
      )}

      {/* Pagination Controls - Desktop */}
      {products.length > 0 && (
        <div className="hidden md:block bg-white rounded-lg shadow-sm p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <span className="text-sm text-gray-600">Total: {totalProducts}</span>
              <select
                value={limit}
                onChange={(e) => { setPage(1); setLimit(Number(e.target.value)); }}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-600"
              >
                <option value={5}>5 / page</option>
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
              </select>
            </div>
            <div className="flex justify-center items-center space-x-4">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  page <= 1 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-orange-600 text-white hover:bg-orange-700'
                }`}
              >
                Previous
              </button>
              <span className="text-sm text-gray-700">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  page >= totalPages 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-orange-600 text-white hover:bg-orange-700'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      <ProductForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
        product={editingProduct}
        onNavigateToCategories={onNavigateToCategories}
      />
    </div>
  );
};

export default ProductList;